import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { fetchManufacturerTransactionStats } from '../../../../services/Http/ManufacturerApi';
import { toast } from 'react-toastify';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ManufacturerTransactionsOverview = ({ period }) => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [records, setRecords] = useState([]);
    const [totals, setTotals] = useState(null);
    const [meta, setMeta] = useState({ total: 0, per_page: 25, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [sort, setSort] = useState({ sortBy: 'date', sortDirection: 'desc' });

    const loadStats = useCallback(async (page = meta.current_page, perPage = meta.per_page, sortOptions = sort) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerTransactionStats({
                period,
                page,
                perPage,
                sortBy: sortOptions.sortBy,
                sortDirection: sortOptions.sortDirection,
            });
            setRecords(response.stats.data || []);
            setMeta({
                total: response.stats.total,
                per_page: response.stats.per_page,
                current_page: response.stats.current_page,
            });
            setTotals(response.totals);
        } catch (error) {
            toast.error(t('ManufacturerTransactions.errors.loadStats'));
        } finally {
            setLoading(false);
        }
    }, [period, meta.current_page, meta.per_page, sort, t]);

    useEffect(() => {
        loadStats(1, meta.per_page, sort);
    }, [period]);

    const columns = useMemo(() => ([
        {
            name: t('ManufacturerTransactions.table.columns.date'),
            selector: (row) => new Date(row.date).toLocaleDateString(),
            sortable: true,
        },
        {
            name: t('ManufacturerTransactions.table.columns.retailer'),
            selector: (row) => row.retailer_name || t('common.unknown'),
            sortable: true,
        },
        {
            name: t('ManufacturerTransactions.table.columns.venue'),
            selector: (row) => row.venue_name || t('common.unknown'),
            sortable: true,
        },
        {
            name: t('ManufacturerTransactions.table.columns.sellInValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_in_value,
                localAmount: row.sell_in_value_local ?? row.sell_in_local,
                localCurrencyId: row.currency_id ?? row.sell_in_currency_id,
                localCurrencyCode: row.currency_code ?? row.sell_in_currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerTransactions.table.columns.sellThroughValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_through_value,
                localAmount: row.sell_through_value_local ?? row.sell_through_local,
                localCurrencyId: row.currency_id ?? row.sell_through_currency_id,
                localCurrencyCode: row.currency_code ?? row.sell_through_currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerTransactions.table.columns.transactions'),
            selector: (row) => row.transactions_count,
            sortable: true,
        },
    ]), [t, formatResolvedAmount]);

    return (
        <div>
            <Cards>
                <StatCard>
                    <label>{t('ManufacturerTransactions.cards.sellIn')}</label>
                    <strong>{formatResolvedAmount({
                        eurAmount: totals?.sell_in_value,
                        localAmount: totals?.sell_in_value_local ?? totals?.sell_in_local,
                        localCurrencyId: totals?.currency_id ?? totals?.sell_in_currency_id,
                        localCurrencyCode: totals?.currency_code ?? totals?.sell_in_currency_code,
                    })}</strong>
                    <small>{t('ManufacturerTransactions.cards.units', { count: totals?.sell_in_quantity || 0 })}</small>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerTransactions.cards.sellThrough')}</label>
                    <strong>{formatResolvedAmount({
                        eurAmount: totals?.sell_through_value,
                        localAmount: totals?.sell_through_value_local ?? totals?.sell_through_local,
                        localCurrencyId: totals?.currency_id ?? totals?.sell_through_currency_id,
                        localCurrencyCode: totals?.currency_code ?? totals?.sell_through_currency_code,
                    })}</strong>
                    <small>{t('ManufacturerTransactions.cards.units', { count: totals?.sell_through_quantity || 0 })}</small>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerTransactions.cards.retailers')}</label>
                    <strong>{totals?.distinct_retailers || 0}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerTransactions.cards.venues')}</label>
                    <strong>{totals?.distinct_venues || 0}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerTransactions.cards.buyers')}</label>
                    <strong>{totals?.distinct_buyers || 0}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerTransactions.cards.transactions')}</label>
                    <strong>{totals?.transactions_count || 0}</strong>
                </StatCard>
            </Cards>

            <DataTable
                columns={columns}
                data={records}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={meta.total}
                paginationPerPage={meta.per_page}
                onChangePage={(page) => loadStats(page, meta.per_page, sort)}
                onChangeRowsPerPage={(perPage, page) => loadStats(page, perPage, sort)}
                onSort={(column, direction) => {
                    const sortBy = column.selector === columns[0].selector ? 'date' : column.selector === columns[3].selector ? 'sell_in_value' : column.selector === columns[4].selector ? 'sell_through_value' : 'transactions_count';
                    const next = { sortBy, sortDirection: direction };
                    setSort(next);
                    loadStats(meta.current_page, meta.per_page, next);
                }}
                sortServer
                highlightOnHover
            />
        </div>
    );
};

export default ManufacturerTransactionsOverview;

const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
    text-align: center;
    background: rgba(130, 22, 146, 0.02);

    label {
        display: block;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        margin-bottom: 0.375rem;
    }

    strong {
        display: block;
        font-size: 1.4rem;
        color: rgba(130, 22, 146, 1);
    }

    small {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }
`;
